import { useRef, useState } from 'react'

import {
  type ActivityItem,
  type ItemResult,
  type SessionDelivery,
} from '@/platform/learning/contract'
import { type LearningService } from '@/platform/learning/service'
import { ERROR_CODE, type ErrorCode } from '@/shared/errors/codes'
import { AppError } from '@/shared/errors/envelope'
import { newUuid } from '@/shared/id/uuid'

import { isChoiceItem, selectionIsComplete } from './choice'
import { ChoiceActivity } from './ChoiceActivity'
import { useFocusOnMount } from './focus'
import { MockBadge } from './MockBadge'
import { SampleContentLabel } from './SampleContentLabel'

/**
 * Shared Learning Session shell (UI v0.2 §4.3): close action, progress, one
 * English instruction with Vietnamese help behind "?" (FR-45), the activity,
 * a check action that cannot submit twice (FR-41) and feedback from the
 * service response (FR-42, FR-146). Only the Choice family is playable in this
 * slice (Execution Principles §7); other types get an honest fallback (FR-144).
 */

/**
 * Submission errors that the same request cannot fix: the answer was already
 * stored, the request conflicts with a stored one, the lesson is finished, or
 * the session or access is gone. Anything else (network, internal) may be
 * retried with the same idempotency key.
 */
const FINAL_SUBMIT_ERRORS: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  ERROR_CODE.ALREADY_SUBMITTED,
  ERROR_CODE.IDEMPOTENCY_CONFLICT,
  ERROR_CODE.INVALID_STATE,
  ERROR_CODE.INVALID_INPUT,
  ERROR_CODE.RESOURCE_NOT_AVAILABLE,
  ERROR_CODE.ACCESS_DENIED,
  ERROR_CODE.UNAUTHENTICATED,
  ERROR_CODE.SESSION_EXPIRED,
])

/** Full width on phones; a centred, fixed width from tablet up. */
const ACTION_BUTTON =
  'h-14 rounded-2xl bg-sky-700 text-lg font-semibold text-white active:bg-sky-800 md:w-80 md:self-center'

export interface SubmittedAnswer {
  readonly selected: readonly string[]
  readonly result: ItemResult
}

export interface LearningSessionProps {
  readonly learning: LearningService
  readonly lessonTitle: string
  readonly delivery: SessionDelivery
  readonly index: number
  /** Answers already checked in this session, by item ID. */
  readonly answers: ReadonlyMap<string, SubmittedAnswer>
  readonly onSubmitted: (itemId: string, answer: SubmittedAnswer) => void
  /** Go to the next item, or finish after the last one. */
  readonly onNext: () => void
  readonly onClose: () => void
}

export function LearningSession({
  learning,
  lessonTitle,
  delivery,
  index,
  answers,
  onSubmitted,
  onNext,
  onClose,
}: LearningSessionProps): React.JSX.Element {
  const item = delivery.items[index]
  const total = delivery.items.length
  const isLast = index === total - 1

  return (
    <div className="mx-auto flex w-full max-w-lg flex-1 flex-col gap-5 px-5 pb-8 md:max-w-2xl">
      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Thoát bài học"
            className="rounded-full text-2xl text-slate-700 dark:text-slate-300"
            onClick={onClose}
          >
            ✕
          </button>
          <h1 lang="en" className="min-w-0 flex-1 text-lg font-semibold wrap-break-word">
            {lessonTitle}
          </h1>
          {delivery.is_mock && <MockBadge />}
        </div>
        {delivery.is_sample_content && <SampleContentLabel />}
        <div className="flex items-center gap-3">
          <div
            role="progressbar"
            aria-label="Tiến độ bài học"
            aria-valuemin={1}
            aria-valuemax={total}
            aria-valuenow={index + 1}
            aria-valuetext={`Câu ${String(index + 1)}/${String(total)}`}
            className="h-3 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
          >
            <div
              className="h-full rounded-full bg-sky-600 dark:bg-sky-400"
              style={{ width: `${String(((index + 1) / total) * 100)}%` }}
            />
          </div>
          <p aria-hidden className="text-base text-slate-700 dark:text-slate-300">
            Câu {index + 1}/{total}
          </p>
        </div>
      </header>

      {item !== undefined && (
        <ItemStep
          key={item.item_id}
          learning={learning}
          sessionId={delivery.session_id}
          item={item}
          answer={answers.get(item.item_id) ?? null}
          isLast={isLast}
          onSubmitted={onSubmitted}
          onNext={onNext}
          onClose={onClose}
        />
      )}
    </div>
  )
}

interface ItemStepProps {
  readonly learning: LearningService
  readonly sessionId: string
  readonly item: ActivityItem
  readonly answer: SubmittedAnswer | null
  readonly isLast: boolean
  readonly onSubmitted: (itemId: string, answer: SubmittedAnswer) => void
  readonly onNext: () => void
  readonly onClose: () => void
}

function ItemStep({
  learning,
  sessionId,
  item,
  answer,
  isLast,
  onSubmitted,
  onNext,
  onClose,
}: ItemStepProps): React.JSX.Element {
  const [selected, setSelected] = useState<readonly string[]>(answer?.selected ?? [])
  const [checking, setChecking] = useState(false)
  const [failed, setFailed] = useState(false)
  // A failure that retrying cannot fix: the child goes back to Child Home.
  const [stopped, setStopped] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [shownAt] = useState(() => Date.now())
  // Synchronous guard: a second tap before React re-renders must not submit.
  const inFlight = useRef(false)
  // One idempotency key per item, reused on retry so a lost response replays
  // the stored result instead of creating a second attempt (FR-41).
  const attemptId = useRef<string | null>(null)
  // Each new item takes focus, so keyboard and screen-reader users start at
  // its title instead of the top of the page.
  const heading = useFocusOnMount<HTMLHeadingElement>()

  const result = answer?.result ?? null
  const locked = checking || failed || stopped || result !== null
  const nextLabel = isLast ? 'Xem kết quả' : 'Câu tiếp theo'

  const check = async () => {
    if (inFlight.current || stopped || result !== null || !isChoiceItem(item)) return
    if (!selectionIsComplete(item, selected)) return
    inFlight.current = true
    attemptId.current ??= newUuid()
    setChecking(true)
    setFailed(false)
    try {
      const response = await learning.submitItem({
        session_id: sessionId,
        item_id: item.item_id,
        client_attempt_id: attemptId.current,
        response: {
          activity_type: item.activity_type,
          selected_option_ids: [...selected],
        },
        elapsed_ms: Math.max(0, Date.now() - shownAt),
        // PO decision (Task 1.1): opening or re-reading the Vietnamese
        // instructions is not a hint; only an answer clue would be. This
        // slice offers no answer clues.
        hinted: false,
      })
      onSubmitted(item.item_id, { selected, result: response.result })
    } catch (error) {
      if (error instanceof AppError && FINAL_SUBMIT_ERRORS.has(error.code)) {
        setStopped(true)
      } else {
        setFailed(true)
      }
    } finally {
      inFlight.current = false
      setChecking(false)
    }
  }

  return (
    <section aria-labelledby={`title-${item.item_id}`} className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <h2
          id={`title-${item.item_id}`}
          ref={heading}
          tabIndex={-1}
          lang="en"
          className="text-base font-semibold text-sky-800 uppercase dark:text-sky-300"
        >
          {item.title}
        </h2>
        <div className="flex items-start gap-3">
          <p lang="en" className="min-w-0 flex-1 text-lg wrap-break-word">
            {item.instruction.en}
          </p>
          <button
            type="button"
            aria-label="Hướng dẫn tiếng Việt"
            aria-expanded={helpOpen}
            aria-controls={`help-${item.item_id}`}
            className="rounded-full border-2 border-sky-700 text-lg font-bold text-sky-800 dark:border-sky-400 dark:text-sky-300"
            onClick={() => {
              setHelpOpen((open) => !open)
            }}
          >
            ?
          </button>
        </div>
        <p
          id={`help-${item.item_id}`}
          hidden={!helpOpen}
          className="rounded-xl bg-sky-50 p-3 text-base wrap-break-word text-sky-950 dark:bg-sky-950 dark:text-sky-50"
        >
          {item.instruction.vi}
        </p>
      </div>

      {isChoiceItem(item) ? (
        <>
          <div lang="en">
            <ChoiceActivity
              item={item}
              selected={selected}
              locked={locked}
              onChange={setSelected}
            />
          </div>

          {stopped && result === null ? (
            <div className="flex flex-col gap-3">
              <p role="alert" className="text-base text-rose-700 dark:text-rose-400">
                Câu trả lời này không gửi được nữa. Con quay về trang chính nhé.
              </p>
              <button type="button" className={ACTION_BUTTON} onClick={onClose}>
                Về trang chính
              </button>
            </div>
          ) : result === null ? (
            <div className="flex flex-col gap-3">
              {!selectionIsComplete(item, selected) && (
                <p
                  id={`choose-hint-${item.item_id}`}
                  className="text-center text-base text-slate-700 dark:text-slate-300"
                >
                  Chọn đáp án để tiếp tục.
                </p>
              )}
              {failed && (
                <p role="alert" className="text-base text-rose-700 dark:text-rose-400">
                  Chưa gửi được câu trả lời. Con thử lại nhé.
                </p>
              )}
              {/* While checking, the button stays focusable (aria-disabled, not
                  disabled) so keyboard focus is not lost if the check fails. */}
              <button
                type="button"
                disabled={!checking && !selectionIsComplete(item, selected)}
                aria-disabled={checking}
                {...(selectionIsComplete(item, selected)
                  ? {}
                  : { 'aria-describedby': `choose-hint-${item.item_id}` })}
                className={`${ACTION_BUTTON} disabled:opacity-40 aria-disabled:opacity-40`}
                onClick={() => void check()}
              >
                {checking ? 'Đang kiểm tra…' : failed ? 'Thử lại' : 'Kiểm tra'}
              </button>
            </div>
          ) : (
            <Feedback result={result} nextLabel={nextLabel} onNext={onNext} />
          )}
        </>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="rounded-xl bg-white p-4 text-lg dark:bg-slate-900">
            Dạng bài này chưa có trong phiên bản hiện tại. Con chuyển sang phần tiếp theo
            nhé.
          </p>
          <button type="button" className={ACTION_BUTTON} onClick={onNext}>
            {isLast ? 'Xem kết quả' : 'Bỏ qua'}
          </button>
        </div>
      )}
    </section>
  )
}

/**
 * Feedback exactly as returned by the service. Correctness is shown only for
 * a `graded` result; explanation and answer only when the service sent them.
 */
function Feedback({
  result,
  nextLabel,
  onNext,
}: {
  result: ItemResult
  nextLabel: string
  onNext: () => void
}): React.JSX.Element {
  // Focus moves to the feedback heading, which announces the outcome and puts
  // the details and the continue button next in reading and tab order.
  const heading = useFocusOnMount<HTMLHeadingElement>()

  const graded = result.outcome === 'graded' ? result : null
  const tone =
    graded === null
      ? 'border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900'
      : graded.is_correct
        ? 'border-emerald-600 bg-emerald-50 dark:border-emerald-500 dark:bg-emerald-950'
        : 'border-amber-500 bg-amber-50 dark:border-amber-500 dark:bg-amber-950'

  return (
    <div className="flex flex-col gap-4">
      <section
        aria-labelledby="item-feedback"
        className={`flex flex-col gap-2 rounded-2xl border-2 p-4 wrap-break-word ${tone}`}
      >
        <h3 id="item-feedback" ref={heading} tabIndex={-1} className="text-xl font-bold">
          {graded !== null && (
            <span aria-hidden>{graded.is_correct ? '🎉 ' : '💡 '}</span>
          )}
          {graded === null
            ? 'Đã ghi nhận câu trả lời'
            : graded.is_correct
              ? 'Đúng rồi!'
              : 'Chưa đúng'}
        </h3>
        {graded !== null && (
          <>
            <p lang="en" className="text-lg">
              {graded.feedback.message_en}
            </p>
            {!graded.is_correct && graded.feedback.correct_answer_text !== null && (
              <p className="text-lg">
                Đáp án đúng:{' '}
                <span lang="en" className="font-semibold">
                  {graded.feedback.correct_answer_text}
                </span>
              </p>
            )}
            {graded.feedback.explanation_vi !== null && (
              <p className="text-base text-slate-800 dark:text-slate-200">
                {graded.feedback.explanation_vi}
              </p>
            )}
          </>
        )}
        {result.outcome === 'recorded' && (
          <p className="text-base">Kết quả sẽ có khi con làm xong bài.</p>
        )}
      </section>
      <button type="button" className={ACTION_BUTTON} onClick={onNext}>
        {nextLabel}
      </button>
    </div>
  )
}
